import { CrowdHandlerStorage } from "../types/crowdhandler";
import { PositionData } from "../types/crowdhandler";
import {SavePositionsParams} from '../types/crowdhandler';

export interface PositionSample {
    t: number;   // ms timestamp of the poll
    pos: number; // server position at that poll
}

export interface CookieEntry {
    firstPosition: number | null;        // first position ever seen
    firstRecorded: number;        // ms timestamp when firstPosition was recorded
    lastRecorded: number | null;         // ms timestamp of the last server response we applied
    mostRecentEstimate: number | null;   // our last *displayed* (derived) position estimate
    mostRecentPosition: number | null;   // last *server* position seen (not null)
    samples?: PositionSample[];           // trailing window of {t, pos} used for the rate calc
  }

const COOKIE_NAME = "crowdhandler_positions"; // cookie name

// Trailing window the rate estimate is averaged over. Long enough to ride out
// the noisy, batchy realized rate (and timeout-expiry bursts) without jitter,
// short enough that a slow early stretch stops dragging the estimate up. The
// 8-minute value was picked by sweeping window length in eta-window-sweep.mjs.
export const SAMPLE_WINDOW_MS = 8 * 60 * 1000;

// Hard cap on the number of samples retained, independent of the time window.
// Sample count is windowDuration / pollTTL, so a short TTL would otherwise grow
// the array (and the cookie) without bound — at a 5s TTL the 8-min window would
// hold ~97 samples and blow past the ~4KB cookie limit. At the normal ~55s TTL
// the window holds ~9 samples, so this cap (16) never triggers in practice; it
// is purely a backstop. calculateEstimate only ever reads the oldest and newest
// in-window samples, so thinning the middle costs nothing.
export const MAX_SAMPLES = 16;

/**
 * Append a poll sample to the trailing window and prune anything older than
 * SAMPLE_WINDOW_MS, always retaining at least two samples so a rate can still
 * be computed. If the count still exceeds MAX_SAMPLES (only possible under a
 * pathologically short poll TTL), thin from the middle so the oldest and newest
 * — and therefore the full window span — are preserved. Pure: returns a new
 * array, never mutates the input.
 */
export function appendSample(samples: PositionSample[] | undefined, t: number, pos: number): PositionSample[] {
    const next = Array.isArray(samples) ? samples.slice() : [];
    // Defensive against a double poll landing on the same timestamp (init path).
    if (next.length && next[next.length - 1].t === t) {
        next[next.length - 1] = { t, pos };
    } else {
        next.push({ t, pos });
    }
    const cutoff = t - SAMPLE_WINDOW_MS;
    while (next.length > 2 && next[0].t < cutoff) next.shift();
    // Backstop: thin from the middle (never the oldest or newest) so the count is
    // bounded while the samples stay spread across the window. Removing the middle
    // rather than near [0] avoids leaving a gap that would collapse the span once
    // the oldest ages out. Under a sustained pathologically short TTL the effective
    // span settles around half the window (~5 min for the 8-min/16-sample config) —
    // coarser, but still well above the ~2-min noise the window exists to smooth.
    while (next.length > MAX_SAMPLES) next.splice(Math.floor(next.length / 2), 1);
    return next;
}

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp("(^|; )" + encodeURIComponent(name) + "=([^;]*)"));
    return m ? decodeURIComponent(m[2]) : null;
}

function writeCookie(name: string, value: string) {
    document.cookie =
        encodeURIComponent(name) +
        "=" +
        encodeURIComponent(value) +
        ";path=/;SameSite=Lax";
}

function savePositionsRoot(root: { positions: Record<string, CookieEntry> }) {
    writeCookie(COOKIE_NAME, JSON.stringify(root));
}

function loadPositionsRoot(): { positions: Record<string, CookieEntry> } {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) return { positions: {} };
    try {
        const parsed = JSON.parse(raw);
        // Ensure shape
        if (!parsed || typeof parsed !== "object" || typeof parsed.positions !== "object") {
            return { positions: {} };
        }
        return parsed;
    } catch {
        return { positions: {} };
    }
}

export function loadTokenEntry(token: string): CookieEntry | null {
  const root = loadPositionsRoot();
  const entry = root.positions?.[token];
  if (!entry || typeof entry !== "object") return null;
  return entry;
}

export function saveTokenEntry(token: string, entry: CookieEntry) {
  const root = loadPositionsRoot();
  root.positions[token] = entry;
  savePositionsRoot(root);
}

export function deleteTokenEntry(token: string) {
  const root = loadPositionsRoot();
  delete root.positions[token];
  savePositionsRoot(root);
}

export function persistVisibleEstimate(token: string, currentDisplay: number | null) {
  if (currentDisplay == null || !Number.isFinite(currentDisplay)) return;
  const entry = loadTokenEntry(token);
  if (!entry) return;
  const now = Date.now();
  const updated: CookieEntry = {
    ...entry,
    lastRecorded: now,
    mostRecentPosition: Math.max(0, Math.floor(currentDisplay)),
  };

  saveTokenEntry(token, updated);
}

const getCookieStorage = (
    args: {
        item?: 'countdown' | 'positions' | 'token' | 'emailMe',
        nestedKey?: string | null,
        nestedKey2?: string | null
    }
) : CrowdHandlerStorage | PositionData | any => {

    let { item, nestedKey, nestedKey2 } = args;
    let crowdhandlerStorage: CrowdHandlerStorage = {
        countdown: {},
        positions: {},
        token: {},
        emailMe: {}
    };

    try {
        const crowdhandlerStorageJSON = sessionStorage.getItem("crowdhandler");
        //Parse the JSON object.
        crowdhandlerStorage = {
            ...crowdhandlerStorage,  // Keep the default values
            ...JSON.parse(crowdhandlerStorageJSON || '{}')  // Override with parsed values
        };
    } catch (error) {
        console.error(error);
    }

    //Return the entire object if a specific item isn't requested.
    if (!item) {
        return crowdhandlerStorage;
    }

    const storageItem = crowdhandlerStorage[item];
    if (!storageItem) {
        return undefined;
    }

    if (nestedKey && nestedKey2) {
        const nestedItem = storageItem[nestedKey];
        if (nestedItem && typeof nestedItem === 'object' && nestedKey2 in nestedItem) {
            return nestedItem[nestedKey2 as keyof typeof nestedItem];
        }
        return undefined;
    } else if (nestedKey) {
        return storageItem[nestedKey];
    } else {
        return storageItem;
    }
}

const setCookieStorage = (cookie_name: string, value: string, tokenNamespace: string) => {

    const cookie = getCookieStorageSkeleton(cookie_name);
    cookie[tokenNamespace] = value;
    document.cookie = `${cookie_name}=${encodeURIComponent(JSON.stringify(cookie))}; path=/`;
}

const getCookiePositionsByTokenNamespace = (tokenNamespace: string) => {
    const cookieValue = getCookieStorageSkeleton("crowdhandler_positions");

    if (!cookieValue || !cookieValue.positions[tokenNamespace]) {
        return undefined;
    }

    return cookieValue.positions[tokenNamespace];
}

const setCookiePositionsByTokenNamespace = (tokenNamespace: string, value: object) => {
    let cookieValue = getCookieStorageSkeleton("crowdhandler_positions");

    if(!cookieValue){
        cookieValue = { positions: {} };
    }

    cookieValue.positions[tokenNamespace] = value;
    document.cookie = `crowdhandler_positions=${encodeURIComponent(JSON.stringify(cookieValue))}; path=/`;
}

const getCookieStorageSkeleton = (cookie_name: string) => {
    const cookieValue = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(row => row.startsWith(`${cookie_name}=`));

    if (!cookieValue) return null;
    
    try {
        const value = cookieValue.split('=').slice(1).join('='); // Handle values that might contain '='
        return value ? JSON.parse(decodeURIComponent(value)) : null;
    } catch (e) {
        console.error('Error parsing cookie value:', e);
        return null;
    }
}

const createCookieStorageSkeleton = (cookie_name: string) => {

    const Obj = {
        positions: {}
    }
    const cookieValue = JSON.stringify(Obj);
    // Set cookie with no expiration (session cookie)
    document.cookie = `${cookie_name}=${encodeURIComponent(cookieValue)}; path=/`;
}

const savepositionsByTokenNamespace = (tokenNamespace: string, data: SavePositionsParams): void => {
    if (!data.storageName) {
        console.error("Invalid storageName provided to savePositions");
        return;
    }

    let crowdhandlerStorage: any = undefined;

    try {
        crowdhandlerStorage = getCookiePositionsByTokenNamespace(data.storageName);
    } catch (error) {
        console.error(error);
        return undefined;
    }

    let cookie_value = {}

    // Create the object if it doesn't exist yet.
    if (!crowdhandlerStorage) {
        cookie_value = {
            firstPosition: data.position,
            firstRecorded: data.firstRecorded,
            lastRecorded: Date.now(),
            mostRecentEstimate: data.mostRecentEstimate,
            mostRecentPosition: data.lastPosition,
        }
    } else {
        cookie_value = {
            ...crowdhandlerStorage,
            mostRecentEstimate: data.mostRecentEstimate,
            mostRecentPosition: data.position,
            lastRecorded: Date.now(),
        }

    }

    setCookiePositionsByTokenNamespace(data.storageName, cookie_value);
}


export { getCookieStorage, createCookieStorageSkeleton, getCookieStorageSkeleton, setCookieStorage, getCookiePositionsByTokenNamespace, setCookiePositionsByTokenNamespace, savepositionsByTokenNamespace };
